import { baseApi, API_TAGS, createEndpointBuilder } from '@/shared/api';
import { type Contact, type ContactsSearchParams, type OrganizationContactsParams } from '../model/types';

const contactsEndpoint = createEndpointBuilder('/api/v2/contacts');
const organizationsEndpoint = createEndpointBuilder('/api/v2/organizations');

export const contactsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContacts: builder.query<Contact[], ContactsSearchParams>({
      query: (params) => ({
        url: contactsEndpoint(''),
        params
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: API_TAGS.CONTACTS as const, id })), API_TAGS.CONTACTS]
          : [API_TAGS.CONTACTS]
    }),

    getOrganizationContacts: builder.query<Contact[], OrganizationContactsParams>({
      query: ({ orgId, projectId, siteId, q }) => ({
        url: organizationsEndpoint(`/${orgId}/contacts`),
        params: { projectId, siteId, q }
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: API_TAGS.CONTACTS as const, id })), API_TAGS.CONTACTS]
          : [API_TAGS.CONTACTS]
    }),

    createContact: builder.mutation<Contact, Partial<Contact> & { projectId: number; siteId: number }>({
      query: ({ projectId, siteId, ...body }) => ({
        url: contactsEndpoint(''),
        method: 'POST',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: [API_TAGS.CONTACTS]
    }),

    updateContact: builder.mutation<Contact, Contact & { projectId: number; siteId: number }>({
      query: ({ id, projectId, siteId, ...body }) => ({
        url: contactsEndpoint(`/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: API_TAGS.CONTACTS, id }, API_TAGS.CONTACTS]
    })
  })
});

export const {
  useGetContactsQuery,
  useGetOrganizationContactsQuery,
  useCreateContactMutation,
  useUpdateContactMutation
} = contactsApi;
